/**
 * Capacity Calculator Module
 * Handles the ADHD-aware capacity calculator functionality
 */
export class CapacityCalculator {
  constructor(dataManager) {
    this.data = dataManager;
  }

  openCapacityCalculator() {
    const modal = document.createElement('div');
    modal.className = 'capacity-calculator-modal';
    modal.innerHTML = `
      <div class="modal-backdrop"></div>
      <div class="calculator-container">
        <div class="calculator-header">
          <h3>ADHD-Aware Capacity Calculator</h3>
          <p>Let's figure out how much energy this task really needs</p>
          <button class="modal-close" data-action="close">×</button>
        </div>

        <div class="calculator-steps">
          <!-- Step 1: Basic Info -->
          <div class="calc-step active" data-step="1">
            <h4>What's the task?</h4>
            <input type="text" id="task-name" placeholder="e.g., Call doctor's office" class="calc-input">
            <label>Base time estimate (minutes):</label>
            <input type="number" id="base-duration" value="15" min="1" max="480" class="calc-input">
          </div>

          <!-- Step 2: Prep Energy -->
          <div class="calc-step" data-step="2">
            <h4>⚒️ Prep Energy</h4>
            <p class="step-desc">Tasks often need setup time and mental preparation</p>

            <div class="factor-group">
              <label class="factor-item">
                <input type="checkbox" data-factor="find-materials" data-weight="25">
                <span class="factor-text">Need to find/gather materials or info</span>
                <span class="factor-impact">+25%</span>
              </label>

              <label class="factor-item">
                <input type="checkbox" data-factor="unfamiliar" data-weight="50">
                <span class="factor-text">This is new or unfamiliar to me</span>
                <span class="factor-impact">+50%</span>
              </label>

              <label class="factor-item">
                <input type="checkbox" data-factor="research" data-weight="75">
                <span class="factor-text">Need to research how to do this</span>
                <span class="factor-impact">+75%</span>
              </label>
            </div>
          </div>

          <!-- Step 3: Barriers -->
          <div class="calc-step" data-step="3">
            <h4>🚧 Barriers & Challenges</h4>
            <p class="step-desc">What makes this task emotionally or cognitively demanding?</p>

            <div class="factor-group">
              <label class="factor-item">
                <input type="checkbox" data-factor="strangers" data-weight="100">
                <span class="factor-text">Involves calling/emailing strangers</span>
                <span class="factor-impact">+100%</span>
              </label>

              <label class="factor-item">
                <input type="checkbox" data-factor="rejection" data-weight="75">
                <span class="factor-text">Risk of rejection or criticism</span>
                <span class="factor-impact">+75%</span>
              </label>

              <label class="factor-item">
                <input type="checkbox" data-factor="boring" data-weight="50">
                <span class="factor-text">This task is boring/tedious for me</span>
                <span class="factor-impact">+50%</span>
              </label>

              <label class="factor-item">
                <input type="checkbox" data-factor="distracting" data-weight="25">
                <span class="factor-text">I'm in a distracting environment</span>
                <span class="factor-impact">+25%</span>
              </label>
            </div>
          </div>

          <!-- Step 4: Hidden Complexity -->
          <div class="calc-step" data-step="4">
            <h4>🕳️ Hidden Complexity</h4>
            <p class="step-desc">ADHD brains often expand simple tasks in unexpected ways</p>

            <div class="factor-group">
              <label class="factor-item">
                <input type="checkbox" data-factor="hyperfocus" data-weight="200">
                <span class="factor-text">I might hyperfocus and lose track of time</span>
                <span class="factor-impact">+200%</span>
              </label>

              <label class="factor-item">
                <input type="checkbox" data-factor="perfectionism" data-weight="150">
                <span class="factor-text">I tend to perfectionism spirals on this type</span>
                <span class="factor-impact">+150%</span>
              </label>

              <label class="factor-item">
                <input type="checkbox" data-factor="improve" data-weight="100">
                <span class="factor-text">I'll probably want to 'improve' or optimize it</span>
                <span class="factor-impact">+100%</span>
              </label>

              <label class="factor-item">
                <input type="checkbox" data-factor="rabbit-hole" data-weight="300">
                <span class="factor-text">This could lead to rabbit hole research</span>
                <span class="factor-impact">+300%</span>
              </label>
            </div>
          </div>

          <!-- Step 5: Current State -->
          <div class="calc-step" data-step="5">
            <h4>🔋 Current Energy State</h4>
            <p class="step-desc">How are you feeling right now?</p>

            <div class="energy-state-options">
              <label class="energy-option">
                <input type="radio" name="energy-state" value="1" checked>
                <span class="energy-label">🔋 High Energy</span>
                <span class="energy-desc">I'm feeling sharp, focused, and motivated</span>
              </label>

              <label class="energy-option">
                <input type="radio" name="energy-state" value="1.5">
                <span class="energy-label">⚡ Medium Energy</span>
                <span class="energy-desc">I'm doing okay, typical energy levels</span>
              </label>

              <label class="energy-option">
                <input type="radio" name="energy-state" value="2">
                <span class="energy-label">🪫 Low Energy</span>
                <span class="energy-desc">I'm tired, things feel harder than usual</span>
              </label>

              <label class="energy-option">
                <input type="radio" name="energy-state" value="3">
                <span class="energy-label">😵 Burnt Out</span>
                <span class="energy-desc">Everything feels overwhelming and exhausting</span>
              </label>
            </div>
          </div>

          <!-- Results -->
          <div class="calc-step" data-step="results" style="display: none;">
            <h4>📊 Your Calculated Capacity</h4>
            <div class="results-container">
              <div class="result-summary">
                <div class="result-base">
                  <span class="result-label">Base time:</span>
                  <span class="result-value" id="result-base">15 min</span>
                </div>
                <div class="result-adjusted">
                  <span class="result-label">Adjusted time:</span>
                  <span class="result-value" id="result-adjusted">22 min</span>
                </div>
                <div class="result-multiplier">
                  <span class="result-label">Energy multiplier:</span>
                  <span class="result-value" id="result-multiplier">1.5x</span>
                </div>
              </div>

              <div class="result-explanation">
                <p id="result-explanation">Based on your selections, this task will likely take longer due to ADHD-related factors.</p>
              </div>

              <div class="result-recommendations">
                <h5>💡 Recommendations</h5>
                <ul id="result-recommendations">
                  <li>Consider breaking this into smaller chunks</li>
                  <li>Set a timer to avoid hyperfocus traps</li>
                  <li>Prepare materials in advance to reduce setup time</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Navigation -->
        <div class="calculator-nav">
          <button class="calc-btn calc-btn--secondary" id="calc-back" style="display: none;">← Back</button>
          <div class="calc-progress">
            <span class="step-counter">Step <span id="current-step">1</span> of 5</span>
          </div>
          <button class="calc-btn calc-btn--primary" id="calc-next">Next →</button>
          <button class="calc-btn calc-btn--success" id="calc-finish" style="display: none;">Add Task</button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);
    this.initCalculator(modal);
  }

  initCalculator(modal) {
    const steps = modal.querySelectorAll('.calc-step');
    const backBtn = modal.querySelector('#calc-back');
    const nextBtn = modal.querySelector('#calc-next');
    const finishBtn = modal.querySelector('#calc-finish');
    const stepCounter = modal.querySelector('#current-step');

    let currentStep = 1;
    const maxSteps = 5;
    let calculatorData = {
      taskName: '',
      baseDuration: 15,
      factors: {},
      energyState: 1.5,
    };

    const updateNavigation = () => {
      backBtn.style.display = currentStep > 1 ? 'block' : 'none';
      nextBtn.style.display = currentStep < maxSteps ? 'block' : 'none';
      finishBtn.style.display = currentStep === maxSteps ? 'block' : 'none';
      stepCounter.textContent = currentStep;
    };

    const showStep = (stepNumber) => {
      steps.forEach(step => {
        step.classList.toggle('active', step.dataset.step == stepNumber);
      });
      currentStep = stepNumber;
      updateNavigation();
    };

    const calculateResult = () => {
      let multiplier = 1;

      // Add factor weights
      Object.values(calculatorData.factors).forEach(weight => {
        multiplier += weight / 100;
      });

      // Apply energy state multiplier
      multiplier *= calculatorData.energyState;

      const adjustedDuration = Math.round(calculatorData.baseDuration * multiplier);

      return {
        baseDuration: calculatorData.baseDuration,
        adjustedDuration,
        multiplier,
      };
    };

    const showResults = () => {
      const result = calculateResult();

      modal.querySelector('#result-base').textContent = `${result.baseDuration} min`;
      modal.querySelector('#result-adjusted').textContent = `${result.adjustedDuration} min`;
      modal.querySelector('#result-multiplier').textContent = `${result.multiplier.toFixed(1)}x`;

      const explanation = modal.querySelector('#result-explanation');
      const recommendations = modal.querySelector('#result-recommendations');

      if (result.multiplier > 2) {
        explanation.textContent = 'This task requires significant ADHD-aware adjustment. Consider breaking it into smaller, more manageable pieces.';
        recommendations.innerHTML = `
          <li>Break into 2-3 smaller tasks</li>
          <li>Set a strict time limit</li>
          <li>Use body doubling if possible</li>
          <li>Prepare all materials beforehand</li>
        `;
      } else if (result.multiplier > 1.5) {
        explanation.textContent = 'This task will take longer than the base estimate due to ADHD-related factors.';
        recommendations.innerHTML = `
          <li>Set a timer for the adjusted time</li>
          <li>Minimize distractions before starting</li>
          <li>Have a clear stopping point in mind</li>
        `;
      } else {
        explanation.textContent = 'This task should be manageable within the estimated time.';
        recommendations.innerHTML = `
          <li>You've got this!</li>
          <li>Set a gentle timer as a reminder</li>
          <li>Celebrate completing it</li>
        `;
      }

      showStep('results');
    };

    // Navigation handlers
    backBtn.addEventListener('click', () => {
      if (currentStep > 1) {
        showStep(currentStep - 1);
      }
    });

    nextBtn.addEventListener('click', () => {
      if (currentStep < maxSteps) {
        // Collect data from current step
        if (currentStep === 1) {
          const taskName = modal.querySelector('#task-name').value.trim();
          const baseDuration = parseInt(modal.querySelector('#base-duration').value, 10);

          if (!taskName || baseDuration < 1) {
            alert('Please fill in the task name and base duration');
            return;
          }

          calculatorData.taskName = taskName;
          calculatorData.baseDuration = baseDuration;
        }

        if (currentStep === maxSteps) {
          // Get energy state
          const energyStateInput = modal.querySelector('input[name="energy-state"]:checked');
          calculatorData.energyState = parseFloat(energyStateInput?.value || 1.5);

          showResults();
        } else {
          showStep(currentStep + 1);
        }
      }
    });

    finishBtn.addEventListener('click', () => {
      const result = calculateResult();

      // Add the calculated task
      const taskData = {
        name: calculatorData.taskName,
        duration: result.adjustedDuration,
        category: 'calculated',
        notes: `Calculated with ${result.multiplier.toFixed(1)}x multiplier from ${result.baseDuration}min base`,
      };

      // This would typically call the tasks module to add the task
      if (typeof this.onTaskAdded === 'function') {
        this.onTaskAdded(taskData);
      }

      document.body.removeChild(modal);
    });

    // Factor collection
    modal.addEventListener('change', (e) => {
      if (e.target.type === 'checkbox' && e.target.dataset.factor) {
        const factor = e.target.dataset.factor;
        const weight = parseInt(e.target.dataset.weight, 10);
        calculatorData.factors[factor] = e.target.checked ? weight : 0;
      }
    });

    // Close modal
    modal.addEventListener('click', (e) => {
      if (e.target.classList.contains('modal-backdrop') ||
          e.target.classList.contains('modal-close')) {
        document.body.removeChild(modal);
      }
    });

    updateNavigation();
  }

  addCalculatedTask(calculatorData) {
    // This method would be called by the main component
    // For now, just return the task data
    return {
      name: calculatorData.taskName,
      duration: calculatorData.adjustedDuration,
      category: 'calculated',
      notes: `Calculated task with ${calculatorData.multiplier.toFixed(1)}x multiplier`,
    };
  }
}